import { useCallback, useEffect } from "react";

import { EMBED_EVENT_PREFIX as EVENT_PREFIX } from "@hl/shared-features/lib/features/embed/events";

interface MintStateCommand extends CustomEvent {
  detail: {
    collectionId: string;
    quantity?: number;
    seed?: string;
    donationAmount?: string;
  };
}

interface SeedRefreshStateCommand extends CustomEvent {
  detail: {
    collectionId: string;
    seed: string;
    tokenId: string;
  };
}

export enum EmbedCommands {
  UpdateMintState = "update-mint-state",
  UpdateSeedRefreshState = "update-seed-refresh-state",
}

export const useMintEmbedCommandListeners = ({
  collectionId,
  setNumTokensToMint,
  setMintData,
  setDonationAmount,
}: {
  collectionId: string;
  setNumTokensToMint: (quantity: number) => void;
  setMintData: (mintData: string) => void;
  setDonationAmount: (donationAmount: string) => void;
}) => {
  const handleMintStateCommand = useCallback(
    (event: MintStateCommand) => {
      console.log(`received: ${event.type}`, event.detail);
      if (event.detail.collectionId === collectionId && event.detail.quantity) {
        setNumTokensToMint(event.detail.quantity);
      }
      if (event.detail.collectionId === collectionId && event.detail.seed) {
        setMintData(event.detail.seed);
      }
      if (
        event.detail.collectionId === collectionId &&
        event.detail.donationAmount
      ) {
        setDonationAmount(event.detail.donationAmount);
      }
    },
    [collectionId, setMintData, setNumTokensToMint, setDonationAmount]
  );

  useEffect(() => {
    const eventName = `${EVENT_PREFIX}:${EmbedCommands.UpdateMintState}`;
    window.addEventListener(eventName, handleMintStateCommand as EventListener);

    return () => {
      window.removeEventListener(
        eventName,
        handleMintStateCommand as EventListener
      );
    };
  }, [handleMintStateCommand]);
};

export const useSeedRefreshEmbedCommandListeners = ({
  collectionId,
  setSeed,
  setTokenId,
}: {
  collectionId: string;
  setSeed: (seed: string) => void;
  setTokenId: (tokenId: string) => void;
}) => {
  const handleSeedRefreshStateCommand = useCallback(
    (event: SeedRefreshStateCommand) => {
      console.log("update seed refresh state command", event);
      if (event.detail.collectionId === collectionId) {
        setSeed(event.detail.seed);
        setTokenId(event.detail.tokenId);
      }
    },
    [collectionId, setSeed, setTokenId]
  );

  useEffect(() => {
    const eventName = `${EVENT_PREFIX}:${EmbedCommands.UpdateSeedRefreshState}`;
    window.addEventListener(
      eventName,
      handleSeedRefreshStateCommand as EventListener
    );

    return () => {
      window.removeEventListener(
        eventName,
        handleSeedRefreshStateCommand as EventListener
      );
    };
  }, [handleSeedRefreshStateCommand]);
};
